/**
 * Ksting
 *
 * Copyright © 2017 Chris Andersson <chris@wbisbi.com>. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React, { Component } from 'react';
import { shape, string, func, object, array, bool } from 'prop-types';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import classNames from 'clsx';

import { createUrl } from 'utils/urlHelper';

import p from 'polyglot/polyglot';

import {
  GET_PROFILE_SUCCESS,
  getProfile as getProfileAction,
} from 'actions/user';

import { makeGetUserProfile } from 'selectors/getUserProfile';

import SpinnerIcon from 'components/SpinnerIcon/SpinnerIcon';
import ProfileHeader from 'components/ProfileHeader/ProfileHeader';
import ProfileInfo from 'components/ProfileInfo/ProfileInfo';
import Gallery from 'components/Gallery/Gallery';
import BackButton from 'components/BackButton/BackButton';

import styles from './view.scss';

/**
 * This is the View component.
 *
 * @author Chris Andersson <chris@wbisbi.com>
 */
export class View extends Component {
  /**
   * Declare expected prop types.
   *
   * @type {object}
   */
  static propTypes = {
    location: shape({
      pathname: string,
    }).isRequired,
    match: shape({
      params: shape({
        slug: string,
        jobId: string,
      }).isRequired,
    }).isRequired,
    auth: shape({
      token: string.isRequired,
    }).isRequired,
    profile: shape({
      role_data: object.isRequired,
      pictures: shape({
        polaroids: array.isRequired,
        portfolio: array.isRequired,
      }).isRequired,
      is_private: bool,
      slug: string,
      show_request_button: bool,
      fullname: string,
      title: string,
      location_city: string,
      location_country: string,
      talent_presentation: string,
      loading: bool,
      profile_picture: object,
    }).isRequired,
    history: object.isRequired,
    getProfile: func.isRequired,
    profileSlug: string,
  };

  static defaultProps = {
    profileSlug: null,
  };

  componentDidMount() {
    this.getProfile();
  }

  componentDidUpdate(prevProps) {
    const {
      match: { params: prevParams },
    } = prevProps;
    const {
      match: { params },
    } = this.props;

    // New profile.
    if (params.slug !== prevParams.slug) {
      this.getProfile(true);
    }
  }

  getProfile(force = false) {
    const {
      auth: { token },
      match: { params },
      profile,
      profileSlug,
      getProfile,
      history,
    } = this.props;

    const slug = profileSlug || params.slug;
    const hasNewProfile = !force && profile.slug !== slug;

    if (hasNewProfile) {
      getProfile(token, slug, params.jobId).then(({ type, payload }) => {
        if (type === GET_PROFILE_SUCCESS) {
          const { is_private } = payload;

          // Go to signup page if profile is private.
          if (is_private) {
            history.replace(createUrl('signup'));
          }
        }
      });
    }
  }

  getPolaroids = () => {
    const {
      profile: {
        pictures: { polaroids, portfolio },
        roles,
        loading,
      },
    } = this.props;

    if (loading) return null;

    return roles && roles.default_role === 'MODEL'
      ? polaroids
      : portfolio.filter(() => true).slice(0, 3);
  };

  getPortfolioPictures = () => {
    const {
      profile: {
        pictures: { portfolio },
        roles,
        loading,
      },
    } = this.props;

    if (loading) return null;

    return roles && roles.default_role === 'MODEL'
      ? portfolio
      : portfolio.filter(() => true).slice(3, portfolio.length);
  };

  /**
   * Render View.
   *
   * @return {Object}
   */
  render() {
    const {
      profile,
      location: { pathname },
      match: {
        params: { jobId },
      },
    } = this.props;

    const { is_private } = profile;

    if (is_private) return null;

    const lightboxImages = [
      ...profile.pictures.polaroids,
      ...profile.pictures.portfolio,
    ];

    const polaroids = this.getPolaroids();
    const portfolio = this.getPortfolioPictures();

    const isAccountView = pathname.includes('/account');

    if (profile.loading) {
      return (
        <section className={styles.wrapper}>
          <div className={styles.spinner}>
            <SpinnerIcon medium />
          </div>
        </section>
      );
    }

    return (
      <section
        className={classNames(styles.wrapper, {
          [styles.compact]: isAccountView,
        })}
      >
        {!isAccountView && <BackButton />}

        <ProfileHeader jobId={jobId} compact={isAccountView} {...profile} />

        <div className={styles.container}>
          <div className={styles.row}>
            <div className={styles.profileInfo}>
              <ProfileInfo {...profile} />
            </div>
            <div className={styles.polaroids}>
              <Gallery
                images={polaroids}
                lightboxImages={lightboxImages}
                fillContainer
              />
            </div>
          </div>
        </div>

        <div className={styles.container}>
          <div className={styles.row}>
            <section className={styles.gallery}>
              <header className={styles.galleryHeader}>
                <h2>{p.t('portfolio.title.portfolio')}</h2>
              </header>

              <Gallery images={portfolio} lightboxImages={lightboxImages} />
            </section>
          </div>
        </div>
      </section>
    );
  }
}

/**
 * Map state from redux store to props.
 */
const makeMapStateToProps = () => {
  const getUserProfile = makeGetUserProfile();
  const mapStateToProps = (state, props) => {
    return {
      auth: state.auth,
      profile: getUserProfile(state, props),
    };
  };
  return mapStateToProps;
};

export default withRouter(
  connect(makeMapStateToProps, {
    getProfile: getProfileAction,
  })(View)
);
